import type { FC } from 'react'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { EAccessControlValue } from '@frontend/components/agromarket/core/access-control'
import { useOrderingHarvestRequestModalApi } from '@/api/hooks/outputs'
import { BannerFinancingOffer } from '@/components/banners/BannerFinancingOffer'
import { MobileAuthButton } from '@/components/composed'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import WithBreadcrumbs from '@/components/ui/Breadcrumbs/WithBreadcrumbs'
import { transformOptions } from '@/components/ui/fields/helpers/Dropdown'
import { getLocation } from '@/logic/app/reducer'
import { getAuthState, isAuthenticated, isUserAgrarian } from '@/logic/auth/reducer'
import { useData } from '@/logic/data'
import { setHarvestSaleDeliveryBasis } from '@/logic/harvestSale/actions'
import epics from '@/logic/harvestSale/epics'
import { pushHarvestGtmEvent } from '@/logic/metrika/harvest/gtmEvents'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { ApplyingFinancingOfferSuccessModal } from '@/pages/Financing/components'
import {
  HarvestSaleAccessControlProvider,
  useHarvestSaleAccessControl,
} from '@/pages/HarvestSale/components/HarvestSaleAccessControlProvider/HarvestSaleAccessControlProvider'
import { RequestHarvestSaleByTemplateModal } from '@/pages/HarvestSaleLots/components'
import { harvestSaleBreadcrumbs } from '@/pages/HarvestSaleLots/constants'
import type { RootState } from '@/redux/interfaces'
import type { IHarvestSaleCulture, IHarvestSalePageRequest, ISaleRequestByCultureDataResponse, ITemplate } from '@/types/HarvestSaleLots'
import { addNewEpicsLazily } from '@/utils/addNewEpicsLazily'
import { useMount } from '@/utils/hooks'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { HarvestSaleLotsPreview, RequestedAvailableCultures } from './components'
import { messages } from './messages'

addNewEpicsLazily([epics])
addModalToWellKnown({ RequestHarvestSaleByTemplateModal, ApplyingFinancingOfferSuccessModal })

const mapState = (state: RootState) => ({
  isAgrarian: isUserAgrarian()(state),
  shouldOpenHarvestSaleRequestForm: getLocation()(state).hash?.includes('#harvest-sale-request'),
  isUserAuthenticated: isAuthenticated()(state),
  userGtmInfo: getUserGtmInfo()(state),
})

const HarvestSale: FC = () => {
  const dispatch = useDispatch()
  const { userGtmInfo, isAgrarian, shouldOpenHarvestSaleRequestForm, isUserAuthenticated } = useSelector(mapState)
  const { userRegion } = useUserRegion()

  const { isMobile } = useDeviceType()
  const { resolve } = useHarvestSaleAccessControl()

  const { data: availableCultures = [] } = useData<IHarvestSaleCulture[]>('cultures')
  const { data, isLoading: isLoadingCultures } = useData<ISaleRequestByCultureDataResponse>('harvestSalePage')
  const { saleRequestTemplateByCultureData = [], deliveryBasisVariants } = data || {}
  const withBanner = resolve('harvestSalePage.lotsPreview.logisticsServiceBanner') === EAccessControlValue.VISIBLE
  const { openOrderingHarvestRequestModal } = useOrderingHarvestRequestModalApi({ isOwnOffer: true })

  useMount(() => {
    if (shouldOpenHarvestSaleRequestForm) openOrderingHarvestRequestModal()
  })

  useEffect(() => {
    if (deliveryBasisVariants?.length) {
      dispatch(setHarvestSaleDeliveryBasis(transformOptions({ options: deliveryBasisVariants, valueKey: 'id' })))
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryBasisVariants])

  useEffect(() => {
    if (saleRequestTemplateByCultureData.length) {
      const items = saleRequestTemplateByCultureData.reduce(
        (acc: ITemplate[], { saleTemplatesByCulture }, index) =>
          index === 0 ? [...acc, ...saleTemplatesByCulture?.slice(0, 2)] : [...acc, ...saleTemplatesByCulture],
        [],
      )

      pushHarvestGtmEvent({
        items,
        event: 'userEvent',
        eventCategory: 'interactions',
        eventAction: 'viewing',
        eventNonInteraction: '1',
        blockName: 'blockListingOutputs',
        eventLabel: 'lotsOutputs',
        ecommerceType: 'impressions',
        eventStatus: 'success',
        eventEcommerce: 'impressions',
        pagePart: 'content',
        itemContext: 'previewLotsOutputs',
        regionSelected: userRegion.name,
        userGtmInfo,
      })
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(saleRequestTemplateByCultureData)])

  return (
    <>
      <MobileAuthButton visibilityMinOffset={493} />
      {!isMobile && <WithBreadcrumbs breadcrumbs={harvestSaleBreadcrumbs} />}

      <RequestedAvailableCultures
        title={messages.title}
        availableCultures={availableCultures}
        isLoading={isLoadingCultures}
        isAgrarian={isAgrarian}
        isAuthenticated={isUserAuthenticated}
      />

      {(isLoadingCultures ? ([{}, {}] as IHarvestSalePageRequest[]) : saleRequestTemplateByCultureData)?.map(
        ({ cultureData, count, saleTemplatesByCulture }, index) => (
          <HarvestSaleLotsPreview
            key={cultureData?.id || index}
            culture={cultureData}
            isAgrarian={isAgrarian}
            isMobileDevice={isMobile}
            isLoading={isLoadingCultures}
            count={count}
            saleTemplatesByCulture={saleTemplatesByCulture}
            withBanner={withBanner && index === 0}
            isAuthenticated={isUserAuthenticated}
          />
        ),
      )}
      <div className="space-holder-top72">
        <BannerFinancingOffer />
      </div>
    </>
  )
}

const mapStateForContext = (state: RootState) => ({
  auth: getAuthState()(state),
})

const HarvestSaleWithAccessControlProvider = () => {
  const context = useSelector(mapStateForContext)

  return (
    <HarvestSaleAccessControlProvider context={context}>
      <HarvestSale />
    </HarvestSaleAccessControlProvider>
  )
}

export default HarvestSaleWithAccessControlProvider
